import type { JsonObject, SecretProvider, SecretRef, ToolDef } from "@fabric-harness/sdk";
import type { SecretsClient } from "@databricks/sdk-secrets/v1";

export type DatabricksSecretsClient = Pick<SecretsClient, "createScope" | "deleteScope" | "putSecret">;

export class DatabricksSecretsAuthoring {
  constructor(private readonly client: DatabricksSecretsClient) {}
  createScope(scope: string, initialManagePrincipal?: string): Promise<unknown> {
    return this.client.createScope({
        scope,
        ...(initialManagePrincipal ? { initialManagePrincipal } : {}),
      });
  }
  deleteScope(scope: string): Promise<unknown> {
    return this.client.deleteScope({ scope });
  }
  putRawSecret(scope: string, key: string, value: string): Promise<unknown> {
    return this.client.putSecret({
      scope,
      key,
      value: { $case: "stringValue", stringValue: value },
    });
  }
  async putSecret(
    scope: string,
    key: string,
    secretRef: SecretRef,
    provider: SecretProvider,
  ): Promise<unknown> {
    const value = await provider.resolve(secretRef);
    if (value === undefined)
      throw new Error(
        `databricks secrets: provider "${provider.name}" could not resolve ${secretRef.name}.`,
      );
    return this.putRawSecret(scope, key, value);
  }
}
export function databricksSecretsAuthoringTools(
  client: DatabricksSecretsClient,
  provider: SecretProvider,
): ToolDef[] {
  const api = new DatabricksSecretsAuthoring(client);
  return [
    tool(
      "databricks_create_secret_scope",
      scopeSchema(true),
      [{ kind: "secret-scope", path: "/scope" }],
      (i) => api.createScope(i.scope as string, i.initialManagePrincipal as string | undefined),
    ),
    tool(
      "databricks_delete_secret_scope",
      scopeSchema(false),
      [{ kind: "secret-scope", path: "/scope" }],
      (i) => api.deleteScope(i.scope as string),
    ),
    tool(
      "databricks_put_secret",
      putSchema(),
      [
        { kind: "secret-scope", path: "/scope" },
        { kind: "secret-key", path: "/key" },
      ],
      (i) =>
        api.putSecret(
          i.scope as string,
          i.key as string,
          i.secretRef as unknown as SecretRef,
          provider,
        ),
    ),
  ];
}
function tool(
  name: string,
  inputSchema: JsonObject,
  resources: JsonObject[],
  execute: (input: JsonObject) => unknown,
): ToolDef {
  return {
    name,
    description: name.replaceAll("_", " "),
    inputSchema,
    metadata: {
      effect: "write",
      provider: "databricks",
      service: "secrets",
      governance: { resources, secretFields: ["secretRef"] },
    },
    execute,
  };
}
function scopeSchema(create: boolean): JsonObject {
  return {
    type: "object",
    additionalProperties: false,
    properties: {
      scope: { type: "string" },
      ...(create ? { initialManagePrincipal: { type: "string" } } : {}),
    },
    required: ["scope"],
  };
}
function putSchema(): JsonObject {
  return {
    type: "object",
    additionalProperties: false,
    properties: {
      scope: { type: "string" },
      key: { type: "string" },
      secretRef: {
        type: "object",
        additionalProperties: false,
        properties: { kind: { const: "secret" }, name: { type: "string" } },
        required: ["kind", "name"],
      },
    },
    required: ["scope", "key", "secretRef"],
  };
}
